// Words and numbers as the dealer reads them. Money is integer cents everywhere;
// formatCents from the domain draws it.
import { QUOTE_TYPE_LABELS, type Billing, type Cents, type ContractTerm, type OrderStatus, type QuoteStatus, type QuoteType, type StepState } from "@phoneware/lens-domain";
import type { IconName } from "./icons.js";

export type ChipKind = "" | "ok" | "warn" | "stop" | "quiet" | "ink" | "house" | "info";

export interface Look {
  label: string;
  kind: ChipKind;
  icon: IconName | null;
}

export const QUOTE_STATUS: Record<QuoteStatus, Look> = {
  draft: { label: "Draft", kind: "quiet", icon: null },
  needs_approval: { label: "Needs approval", kind: "warn", icon: "triangle-alert" },
  sent: { label: "Sent", kind: "", icon: null },
  viewed: { label: "Viewed", kind: "", icon: "eye" },
  signed: { label: "Signed", kind: "ok", icon: "check" },
  ordered: { label: "Ordered", kind: "", icon: null },
  live: { label: "Live", kind: "ok", icon: "check" },
  lost: { label: "Lost", kind: "quiet", icon: "x" },
};

export const ORDER_STATUS: Record<OrderStatus, Look> = {
  in_progress: { label: "In progress", kind: "", icon: "clock" },
  awaiting_billing_approval: { label: "Waiting on Phoneware", kind: "warn", icon: "clock" },
  needs_attention: { label: "Needs attention", kind: "warn", icon: "triangle-alert" },
  complete: { label: "Complete", kind: "ok", icon: "check" },
};

export const STEP_STATE: Record<StepState, Look> = {
  pending: { label: "Next", kind: "quiet", icon: null },
  running: { label: "In progress", kind: "", icon: "clock" },
  done: { label: "Done", kind: "ok", icon: "check" },
  failed: { label: "Failed", kind: "stop", icon: "triangle-alert" },
};

/** New, MAC (a change quote, plan 7A) and Renewal: the domain's labels, so every screen says the same. */
export const QUOTE_TYPE: Record<QuoteType, string> = QUOTE_TYPE_LABELS;

export const BILLING: Record<Billing, string> = { monthly: "Monthly", one_time: "One-time" };

export const TERMS: ContractTerm[] = [12, 24, 36, 60, "month_to_month"];

/** 10 digits the way people read them: (928) 555-0117. */
export function phoneWords(digits: string): string {
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

export function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export function firstName(name: string): string {
  return name.trim().split(/\s+/)[0] ?? name;
}

export function greeting(now: Date): string {
  const h = now.getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

/** Cents as the text a price box shows: 1250 is "12.50". */
export function centsToDollars(c: Cents): string {
  return (c / 100).toFixed(2);
}

/** What someone typed in a price box, as cents. "$1,234.5" is 123450. null when it is not a price. */
export function parseDollars(text: string): Cents | null {
  const clean = text.replace(/[^0-9.]/g, "");
  if (!clean) return null;
  const num = Number(clean);
  if (!Number.isFinite(num) || num < 0) return null;
  return Math.round(num * 100);
}

/** "2:46 PM". */
export function clock(d: Date): string {
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

/** "just now", "12 min ago", "Today 9:42 AM", "Monday 2:18 PM", then a date. */
export function when(iso: string, now = new Date()): string {
  const then = new Date(iso);
  const diffMs = now.getTime() - then.getTime();
  if (diffMs < 60_000) return "just now";
  if (diffMs < 3600_000) return `${Math.floor(diffMs / 60_000)} min ago`;
  const today = now.toDateString() === then.toDateString();
  if (today) return `Today ${clock(then)}`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (yesterday.toDateString() === then.toDateString()) return `Yesterday ${clock(then)}`;
  const daysDiff = (now.getTime() - then.getTime()) / (1000 * 3600 * 24);
  if (daysDiff < 7) {
    const day = then.toLocaleDateString("en-US", { weekday: "long" });
    return `${day} ${clock(then)}`;
  }
  return then.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

/** The same, mid-sentence: "today at 9:42 AM", "on Monday at 2:18 PM", "on Oct 3". */
export function whenIn(iso: string, now = new Date()): string {
  const text = when(iso, now);
  if (text === "just now") return "just now";
  if (text.endsWith("ago")) return text;
  if (text.startsWith("Today")) return `today at ${text.slice(6)}`;
  if (text.startsWith("Yesterday")) return `yesterday at ${text.slice(10)}`;
  return `on ${text}`;
}

/** A clock time mid-sentence, with the day when it isn't today: "at 8:53 AM", "at 4:41 PM yesterday", "at 2:20 PM on Tuesday", "at 9:05 AM on Oct 3". */
export function atTime(iso: string, now = new Date()): string {
  const then = new Date(iso);
  const time = `at ${clock(then)}`;
  if (now.toDateString() === then.toDateString()) return time;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (yesterday.toDateString() === then.toDateString()) return `${time} yesterday`;
  const days = (now.getTime() - then.getTime()) / 86400000;
  if (days > 0 && days < 7) return `${time} on ${then.toLocaleDateString("en-US", { weekday: "long" })}`;
  return `${time} on ${then.toLocaleDateString("en-US", { month: "short", day: "numeric" })}`;
}

/** A day and a clock time mid-sentence: "today at 9:15 AM", "yesterday at 1:40 PM", "Monday at 2:18 PM", "Oct 17 at 11:05 AM". */
export function dayAt(iso: string, now = new Date()): string {
  const then = new Date(iso);
  const time = clock(then);
  if (now.toDateString() === then.toDateString()) return `today at ${time}`;
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  if (yesterday.toDateString() === then.toDateString()) return `yesterday at ${time}`;
  const days = (now.getTime() - then.getTime()) / 86400000;
  if (days > 0 && days < 7) return `${then.toLocaleDateString("en-US", { weekday: "long" })} at ${time}`;
  return `${then.toLocaleDateString("en-US", { month: "short", day: "numeric" })} at ${time}`;
}

/** A full timestamp for the record: "Oct 14, 2026, 3:42 PM MDT". */
export function stamp(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZoneName: "short",
  });
}

export function sameMonth(iso: string, now = new Date()): boolean {
  const d = new Date(iso);
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export function errorText(e: unknown): string {
  return e instanceof Error ? e.message : "Something went wrong.";
}

/** A plausible email address; the server decides whether it is invited or valid. */
export function isEmail(value: string): boolean {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(value.trim());
}
